import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from '@medical/shared/contexts/AuthContext';
import { hasFeature } from '@medical/shared/utils/features';
import { OfflineSyncProvider } from './patient/contexts/OfflineSyncContext';
import ProtectedRoute from './patient/auth/ProtectedRoute';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import PWAInstallPrompt from '@medical/shared/components/PWAInstallPrompt';
import TTSPlayer from '@medical/shared/components/TTSPlayer';
import Login from './patient/auth/Login';
import PatientApp from './patient/PatientApp';
import DoctorProtectedRoute from './doctor/components/DoctorProtectedRoute';
import DoctorDashboard from './doctor/pages/DoctorDashboard';

const AppRouter = () => {
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();
  const { i18n } = useTranslation();

  const isLoginRoute = location.pathname === '/login' || (location.pathname === '/' && !isAuthenticated);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isAuthenticated) {
      i18n.changeLanguage('en');
      return;
    }

    if (isAuthenticated && user && !hasFeature(user, 'translation')) {
      i18n.changeLanguage('en');
      return;
    }

    if (isAuthenticated && user) {
      const userId = user.patientId || user._id || user.id;
      const stored = userId
        ? localStorage.getItem(`preferredLanguage_${userId}`)
        : localStorage.getItem('preferredLanguage');
      const code = stored || 'en';
      if (userId) {
        localStorage.setItem(`preferredLanguage_${userId}`, code);
      }
      i18n.changeLanguage(code);
    }
  }, [isAuthenticated, user, i18n]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {isLoginRoute ? (
        <Login />
      ) : (
        <ProtectedRoute>
          <PatientApp />
        </ProtectedRoute>
      )}
      {!isLoginRoute && <TTSPlayer />}
      <PWAInstallPrompt />
    </>
  );
};

function App() {
  const location = useLocation();
  if (location.pathname.startsWith('/doctor')) {
    return (
      <DoctorProtectedRoute>
        <DoctorDashboard />
      </DoctorProtectedRoute>
    );
  }

  return (
    <AuthProvider>
      <OfflineSyncProvider>
        <AppRouter />
      </OfflineSyncProvider>
    </AuthProvider>
  );
}

export default App;
